import { useCallback, useMemo } from 'react';
import { useTaskRouteNavigation } from '../use-task-route-navigation';
import {
  getTaskDetailsTabRouteState,
  TASK_ROUTE_KEY,
  type TaskRouteKey,
} from '../task-route.utils';
import { TaskDetailsTab } from './TaskDetailsView.utils';

const TAB_ROUTE_KEY: Record<TaskDetailsTab, TaskRouteKey> = {
  [TaskDetailsTab.Details]: TASK_ROUTE_KEY.details,
  [TaskDetailsTab.Subtasks]: TASK_ROUTE_KEY.subtasks,
  [TaskDetailsTab.Comments]: TASK_ROUTE_KEY.comments,
  [TaskDetailsTab.ActivityLog]: TASK_ROUTE_KEY.activityLog,
};

const getFallbackTab = ({
  isSubtasksActive,
  isCommentsActive,
  isActivityLogActive,
}: ReturnType<typeof getTaskDetailsTabRouteState>): TaskDetailsTab => {
  if (isSubtasksActive) return TaskDetailsTab.Subtasks;
  if (isCommentsActive) return TaskDetailsTab.Comments;
  if (isActivityLogActive) return TaskDetailsTab.ActivityLog;

  return TaskDetailsTab.Details;
};

export const useTaskDetailsNavigation = (taskId: string) => {
  const { pathname, taskPaths, navigateToRoute } = useTaskRouteNavigation();
  const tabRoutes = useMemo(
    () => getTaskDetailsTabRouteState(pathname, taskPaths),
    [pathname, taskPaths]
  );

  const fallbackTab = getFallbackTab(tabRoutes);

  const navigateToSubtasksList = useCallback(
    () => navigateToRoute(TASK_ROUTE_KEY.subtasks, { taskId }),
    [navigateToRoute, taskId]
  );

  const navigateToNewSubtask = useCallback(
    () => navigateToRoute(TASK_ROUTE_KEY.subtasksNew, { taskId }),
    [navigateToRoute, taskId]
  );

  const navigateToEditSubtask = useCallback(
    (subtaskId: string) =>
      navigateToRoute(TASK_ROUTE_KEY.subtasksEdit, { taskId, subtaskId }),
    [navigateToRoute, taskId]
  );

  const navigateToCommentsList = useCallback(
    () => navigateToRoute(TASK_ROUTE_KEY.comments, { taskId }),
    [navigateToRoute, taskId]
  );

  const navigateToNewComment = useCallback(
    () => navigateToRoute(TASK_ROUTE_KEY.commentsNew, { taskId }),
    [navigateToRoute, taskId]
  );

  const handleTabChange = useCallback(
    (tab: TaskDetailsTab) => {
      if (!taskPaths) return;

      const isDetails = tab === TaskDetailsTab.Details;
      const isOnSubRoute =
        tabRoutes.isSubtasksActive ||
        tabRoutes.isCommentsActive ||
        tabRoutes.isActivityLogActive;

      if (isDetails && !isOnSubRoute) return;

      navigateToRoute(TAB_ROUTE_KEY[tab], { taskId, replace: isDetails });
    },
    [taskPaths, navigateToRoute, tabRoutes, taskId]
  );

  return {
    tabRoutes,
    fallbackTab,
    detailsViewKey: fallbackTab,
    navigateToSubtasksList,
    navigateToNewSubtask,
    navigateToEditSubtask,
    navigateToCommentsList,
    navigateToNewComment,
    handleTabChange,
  };
};
